import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Select,
  InputLabel,
  FormControl,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
  Assignment as AssignmentIcon,
  Settings as ParametersIcon,
  Build as EquipmentIcon,
  AccountTree as FlowIcon,
  ViewList as ListIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  MoreVert as MoreVertIcon,
  ContentCopy as DuplicateIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { statusColors } from '../App';
import { apiService } from '../services/apiService';

interface MasterBatchRecord {
  id: number;
  mbr_number: string;
  product_name: string;
  product_code: string;
  version: string;
  batch_size_value: number;
  batch_size_unit: string;
  status: string;
  effective_date: string;
  created_at: string;
  created_by: string;
  approved_at: string | null;
  approved_by: string | null;
}

interface ProcessStep {
  id: number;
  step_number: number;
  step_name: string;
  description: string;
  instructions: string;
  expected_duration_minutes: number;
  is_critical: boolean;
  requires_signature: boolean;
  equipment_class_name: string;
  parameters: ProcessParameter[];
}

interface ProcessParameter {
  id: number;
  parameter_name: string;
  parameter_type: string;
  unit: string;
  target_value: string;
  min_value: number | null;
  max_value: number | null;
  selection_options: any;
  is_critical: boolean;
  is_required: boolean;
  capture_mode: string;
}

const MBREditor: React.FC = () => {
  const [mbrs, setMbrs] = useState<MasterBatchRecord[]>([]);
  const [selectedMBR, setSelectedMBR] = useState<MasterBatchRecord | null>(null);
  const [processSteps, setProcessSteps] = useState<ProcessStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'flow'>('list');
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; mbr: MasterBatchRecord } | null>(null);
  const [newRecipeOpen, setNewRecipeOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importData, setImportData] = useState<any>(null);
  const [importFilename, setImportFilename] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [newRecipe, setNewRecipe] = useState({ mbr_number: '', product_code: '', product_name: '', batch_size_value: '', batch_size_unit: 'tablets', version: '1' });
  const [products, setProducts] = useState<Array<{ id: string; product_code: string; product_name: string }>>([]);

  useEffect(() => {
    fetchMasterBatchRecords();
  }, []);

  const fetchMasterBatchRecords = async () => {
    try {
      setLoading(true);
      const data = await apiService.getMasterBatchRecords();
      setMbrs(data.results || data);
    } catch (error) {
      console.error('Error fetching master batch records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMBRProcessSteps = async (mbrId: number) => {
    try {
      setStepsLoading(true);
      const data = await apiService.getMBRProcessSteps(mbrId);
      setProcessSteps(data);
    } catch (error) {
      console.error('Error fetching process steps:', error);
    } finally {
      setStepsLoading(false);
    }
  };

  const handleViewMBR = async (mbr: MasterBatchRecord) => {
    setSelectedMBR(mbr);
    setDetailsDialogOpen(true);
    await fetchMBRProcessSteps(mbr.id);
  };

  const closeDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedMBR(null);
    setProcessSteps([]);
  };

  // Load products for new recipe dialog
  const loadProducts = async () => {
    try {
      const data = await apiService.getProducts();
      setProducts(data.results || data);
    } catch (e) { console.error(e); }
  };

  // Export MBR as JSON
  const handleExport = async (mbr: MasterBatchRecord) => {
    setMenuAnchor(null);
    try {
      const stepsData = await apiService.getMBRProcessSteps(mbr.id);
      let workflowData = null;
      try {
        const wfRes = await fetch(`/api/workflows/${mbr.id}/`);
        if (wfRes.ok) workflowData = await wfRes.json();
      } catch (e) { /* no workflow */ }

      const exportObj = {
        _format: 'bha-ebr-mbr-v1',
        _exported_at: new Date().toISOString(),
        mbr: {
          mbr_number: mbr.mbr_number,
          product_name: mbr.product_name,
          product_code: mbr.product_code,
          version: mbr.version,
          batch_size_value: mbr.batch_size_value,
          batch_size_unit: mbr.batch_size_unit,
          status: mbr.status,
          effective_date: mbr.effective_date,
        },
        process_steps: stepsData.map((s: ProcessStep) => ({
          step_number: s.step_number,
          step_name: s.step_name,
          description: s.description,
          instructions: s.instructions,
          expected_duration_minutes: s.expected_duration_minutes,
          is_critical: s.is_critical,
          requires_signature: s.requires_signature,
          equipment_class_name: s.equipment_class_name,
          parameters: s.parameters.map((p: ProcessParameter) => ({
            parameter_name: p.parameter_name,
            parameter_type: p.parameter_type,
            unit: p.unit,
            target_value: p.target_value,
            min_value: p.min_value,
            max_value: p.max_value,
            selection_options: p.selection_options,
            is_critical: p.is_critical,
            is_required: p.is_required,
            capture_mode: p.capture_mode,
          })),
        })),
        workflow_definition: workflowData?.workflow_json || null,
      };

      const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${mbr.mbr_number}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: `Exported ${mbr.mbr_number}.json`, severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Export failed', severity: 'error' });
    }
  };

  // Duplicate MBR
  const handleDuplicate = async (mbr: MasterBatchRecord) => {
    setMenuAnchor(null);
    try {
      // Create new MBR via API
      const newMbrNumber = `${mbr.mbr_number}-COPY`;
      const res = await fetch('/api/master-batch-records/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mbr_number: newMbrNumber,
          product: mbr.product_code, // Will need product ID
          version: 1,
          batch_size_value: mbr.batch_size_value,
          batch_size_unit: mbr.batch_size_unit,
          status: 'draft',
          created_by: 'admin',
        }),
      });
      if (res.ok) {
        setSnackbar({ open: true, message: `Duplicated as ${newMbrNumber} (draft)`, severity: 'success' });
        fetchMasterBatchRecords();
      } else {
        const err = await res.text();
        setSnackbar({ open: true, message: `Duplicate failed: ${err}`, severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Duplicate failed', severity: 'error' });
    }
  };

  // Import JSON file handler
  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFilename(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string);
        setImportData(data);
      } catch (err) {
        setSnackbar({ open: true, message: 'Invalid JSON file', severity: 'error' });
      }
    };
    reader.readAsText(file);
  };

  const handleImportConfirm = async () => {
    if (!importData) return;
    try {
      // For now, just show what would be created — full import needs backend endpoint
      setSnackbar({ open: true, message: `Import preview: ${importData.mbr?.mbr_number || 'Unknown'} with ${importData.process_steps?.length || 0} steps. (Backend import endpoint needed for full creation)`, severity: 'success' });
      setImportOpen(false);
      setImportData(null);
      setImportFilename('');
    } catch (e) {
      setSnackbar({ open: true, message: 'Import failed', severity: 'error' });
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    return statusColors[status as keyof typeof statusColors] || '#6B7280';
  };

  const renderParameterDetails = (parameter: ProcessParameter) => (
    <Box key={parameter.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight="bold">
            {parameter.parameter_name}
            {parameter.is_critical && (
              <Chip label="Critical" size="small" color="error" variant="outlined" sx={{ ml: 1 }} />
            )}
            {parameter.is_required && (
              <Chip label="Required" size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Type: {parameter.parameter_type} | Mode: {parameter.capture_mode}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body2">
            <strong>Target:</strong> {parameter.target_value}
            {parameter.unit && ` ${parameter.unit}`}
          </Typography>
          {(parameter.min_value !== null || parameter.max_value !== null) && (
            <Typography variant="body2">
              <strong>Range:</strong> {parameter.min_value || 'N/A'} - {parameter.max_value || 'N/A'}
              {parameter.unit && ` ${parameter.unit}`}
            </Typography>
          )}
          {parameter.selection_options && (
            <Typography variant="body2">
              <strong>Options:</strong> {JSON.stringify(parameter.selection_options)}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            MBR Editor
          </Typography>
          <Typography variant="body1" color="textSecondary">
            View and manage Master Batch Records and their process definitions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setNewRecipeOpen(true); loadProducts(); }}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            New Recipe
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            component="label"
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Import JSON
            <input type="file" accept=".json" hidden onChange={(e) => { handleImportFile(e); setImportOpen(true); }} />
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Master Batch Records
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>MBR Number</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Batch Size</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Effective Date</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Approved</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mbrs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography color="textSecondary">
                          No master batch records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    mbrs.map((mbr) => (
                      <TableRow key={mbr.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {mbr.mbr_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {mbr.product_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {mbr.product_code}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{mbr.version}</TableCell>
                        <TableCell>
                          {mbr.batch_size_value} {mbr.batch_size_unit}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={mbr.status}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(mbr.status),
                              color: 'white',
                              textTransform: 'capitalize',
                              fontWeight: 'medium',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(mbr.effective_date)}
                          </Typography>
                        </TableCell>
                        <TableCell>{mbr.created_by}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {mbr.approved_by ? `${mbr.approved_by} (${formatDate(mbr.approved_at)})` : 'Not approved'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => setMenuAnchor({ el: e.currentTarget, mbr })}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* MBR Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={closeDetailsDialog} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          MBR Details - {selectedMBR?.mbr_number}
        </DialogTitle>
        <DialogContent>
          {selectedMBR && (
            <Box>
              {/* MBR Header Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        {selectedMBR.product_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Product Code: {selectedMBR.product_code}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Version:</strong> {selectedMBR.version}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Batch Size:</strong> {selectedMBR.batch_size_value} {selectedMBR.batch_size_unit}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Chip
                          label={selectedMBR.status}
                          sx={{
                            backgroundColor: getStatusColor(selectedMBR.status),
                            color: 'white',
                            textTransform: 'capitalize',
                            fontWeight: 'medium',
                          }}
                        />
                      </Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>Effective Date:</strong> {formatDate(selectedMBR.effective_date)}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Created:</strong> {formatDate(selectedMBR.created_at)} by {selectedMBR.created_by}
                      </Typography>
                      {selectedMBR.approved_by && (
                        <Typography variant="body2" gutterBottom>
                          <strong>Approved:</strong> {formatDate(selectedMBR.approved_at)} by {selectedMBR.approved_by}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Process Steps Header with View Toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon />
                  Process Steps ({processSteps.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, backgroundColor: '#111827', borderRadius: 1, p: 0.5 }}>
                  <Button
                    size="small"
                    variant={viewMode === 'list' ? 'contained' : 'text'}
                    startIcon={<ListIcon />}
                    onClick={() => setViewMode('list')}
                    sx={{ textTransform: 'none', minWidth: 80 }}
                  >
                    List
                  </Button>
                  <Button
                    size="small"
                    variant={viewMode === 'flow' ? 'contained' : 'text'}
                    startIcon={<FlowIcon />}
                    onClick={() => setViewMode('flow')}
                    sx={{ textTransform: 'none', minWidth: 80 }}
                  >
                    SFC Flow
                  </Button>
                </Box>
              </Box>

              {stepsLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : viewMode === 'flow' ? (
                /* SFC Flow Diagram */
                <Box sx={{ 
                  p: 3, backgroundColor: '#111827', borderRadius: 2, 
                  border: '1px solid #374151', overflowX: 'auto',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                    ISA-88 Sequential Function Chart — {selectedMBR?.product_name}
                  </Typography>
                  
                  {/* Start node */}
                  <Box sx={{
                    width: 120, height: 40, borderRadius: '20px',
                    backgroundColor: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 'bold', fontSize: '0.8rem', color: '#fff', mb: 0,
                  }}>
                    START
                  </Box>

                  {processSteps.map((step, idx) => {
                    const isLast = idx === processSteps.length - 1;
                    return (
                      <React.Fragment key={step.id}>
                        {/* Transition bar + arrow */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box sx={{ width: 2, height: 16, backgroundColor: '#6B7280' }} />
                          <Box sx={{
                            width: 180, height: 24, backgroundColor: '#374151',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.65rem', color: '#9CA3AF', borderLeft: '3px solid #6B7280', borderRight: '3px solid #6B7280',
                          }}>
                            T{step.step_number}: {step.step_number > 1 ? `Step ${step.step_number - 1} complete` : 'Init'}
                          </Box>
                          <Box sx={{ width: 2, height: 16, backgroundColor: '#6B7280' }} />
                          {/* Arrow */}
                          <Box sx={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #6B7280' }} />
                        </Box>

                        {/* Step node */}
                        <Box sx={{
                          width: 280, minHeight: 60, border: `2px solid ${step.is_critical ? '#EF4444' : '#3B82F6'}`,
                          backgroundColor: step.is_critical ? 'rgba(239,68,68,0.08)' : 'rgba(59,130,246,0.08)',
                          borderRadius: 1, p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center',
                          position: 'relative',
                        }}>
                          {/* Step number badge */}
                          <Box sx={{
                            position: 'absolute', top: -10, left: -10,
                            width: 24, height: 24, borderRadius: '50%',
                            backgroundColor: step.is_critical ? '#EF4444' : '#3B82F6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: 'bold', color: '#fff',
                          }}>
                            {step.step_number}
                          </Box>

                          <Typography variant="body2" fontWeight="bold" textAlign="center" sx={{ color: '#F9FAFB' }}>
                            {step.step_name}
                          </Typography>
                          
                          {step.expected_duration_minutes && (
                            <Typography variant="caption" color="text.secondary">
                              ⏱ {step.expected_duration_minutes} min
                            </Typography>
                          )}

                          {/* Tags row */}
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                            {step.is_critical && (
                              <Chip label="CRITICAL" size="small" sx={{ height: 16, fontSize: '0.55rem', backgroundColor: '#EF444430', color: '#EF4444' }} />
                            )}
                            {step.requires_signature && (
                              <Chip label="SIGNATURE" size="small" sx={{ height: 16, fontSize: '0.55rem', backgroundColor: '#F59E0B30', color: '#F59E0B' }} />
                            )}
                            {step.parameters.length > 0 && (
                              <Chip label={`${step.parameters.length} params`} size="small" sx={{ height: 16, fontSize: '0.55rem', backgroundColor: '#3B82F630', color: '#3B82F6' }} />
                            )}
                          </Box>

                          {/* Parameter list inside node (compact) */}
                          {step.parameters.length > 0 && (
                            <Box sx={{ mt: 1, width: '100%', borderTop: '1px solid #374151', pt: 0.5 }}>
                              {step.parameters.slice(0, 3).map(p => (
                                <Typography key={p.id} variant="caption" display="block" sx={{ color: '#9CA3AF', fontSize: '0.6rem' }}>
                                  • {p.parameter_name}: {p.target_value}{p.unit ? ` ${p.unit}` : ''}
                                  {p.min_value !== null && p.max_value !== null ? ` [${p.min_value}–${p.max_value}]` : ''}
                                </Typography>
                              ))}
                              {step.parameters.length > 3 && (
                                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.6rem' }}>
                                  +{step.parameters.length - 3} more...
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </React.Fragment>
                    );
                  })}

                  {/* Final transition + End node */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ width: 2, height: 16, backgroundColor: '#6B7280' }} />
                    <Box sx={{
                      width: 180, height: 24, backgroundColor: '#374151',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.65rem', color: '#9CA3AF', borderLeft: '3px solid #6B7280', borderRight: '3px solid #6B7280',
                    }}>
                      T{processSteps.length + 1}: All steps complete
                    </Box>
                    <Box sx={{ width: 2, height: 16, backgroundColor: '#6B7280' }} />
                    <Box sx={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #6B7280' }} />
                  </Box>

                  <Box sx={{
                    width: 120, height: 40, borderRadius: '20px',
                    backgroundColor: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 'bold', fontSize: '0.8rem', color: '#fff',
                  }}>
                    END
                  </Box>
                </Box>
              ) : (
                <Box>
                  {processSteps.length === 0 ? (
                    <Typography color="textSecondary">
                      No process steps defined
                    </Typography>
                  ) : (
                    processSteps.map((step) => (
                      <Accordion key={step.id} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box display="flex" alignItems="center" gap={2} width="100%">
                            <Typography variant="subtitle1" fontWeight="bold">
                              Step {step.step_number}: {step.step_name}
                            </Typography>
                            {step.is_critical && (
                              <Chip label="Critical" size="small" color="error" variant="outlined" />
                            )}
                            {step.requires_signature && (
                              <Chip label="Requires Signature" size="small" color="warning" variant="outlined" />
                            )}
                            <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
                              {step.parameters.length} parameters
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={8}>
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                Description
                              </Typography>
                              <Typography variant="body1" paragraph>
                                {step.description}
                              </Typography>
                              
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                Instructions
                              </Typography>
                              <Typography variant="body1" paragraph>
                                {step.instructions}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                Step Information
                              </Typography>
                              <Typography variant="body2" gutterBottom>
                                <strong>Expected Duration:</strong> {step.expected_duration_minutes || 'N/A'} minutes
                              </Typography>
                              <Typography variant="body2" gutterBottom>
                                <strong>Equipment Class:</strong> {step.equipment_class_name || 'N/A'}
                              </Typography>
                            </Grid>
                          </Grid>

                          {step.parameters.length > 0 && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <ParametersIcon />
                                <Typography variant="subtitle2" fontWeight="bold">
                                  Process Parameters ({step.parameters.length})
                                </Typography>
                              </Box>
                              {step.parameters.map(renderParameterDetails)}
                            </>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* 3-Dot Row Menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { backgroundColor: '#1F2937', border: '1px solid #374151' } }}
      >
        <MenuItem onClick={() => { if (menuAnchor) { handleViewMBR(menuAnchor.mbr); setMenuAnchor(null); } }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (menuAnchor) handleDuplicate(menuAnchor.mbr); }}>
          <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (menuAnchor) handleExport(menuAnchor.mbr); }}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Export JSON</ListItemText>
        </MenuItem>
      </Menu>

      {/* New Recipe Dialog */}
      <Dialog open={newRecipeOpen} onClose={() => setNewRecipeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1F2937' }}>
          <AddIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> New Recipe
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1F2937' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="MBR Number"
              value={newRecipe.mbr_number}
              onChange={e => setNewRecipe({ ...newRecipe, mbr_number: e.target.value })}
              placeholder="MBR-PROD-DOSE-001"
              helperText="Unique identifier for this batch record"
            />
            <FormControl fullWidth>
              <InputLabel>Product</InputLabel>
              <Select
                value={newRecipe.product_code}
                onChange={e => {
                  const prod = products.find(p => p.product_code === e.target.value);
                  setNewRecipe({
                    ...newRecipe,
                    product_code: e.target.value as string,
                    product_name: prod?.product_name || '',
                  });
                }}
                label="Product"
              >
                {products.map(p => (
                  <MenuItem key={p.id} value={p.product_code}>
                    {p.product_name} ({p.product_code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Batch Size"
                type="number"
                value={newRecipe.batch_size_value}
                onChange={e => setNewRecipe({ ...newRecipe, batch_size_value: e.target.value })}
                sx={{ flex: 2 }}
              />
              <TextField
                label="Unit"
                value={newRecipe.batch_size_unit}
                onChange={e => setNewRecipe({ ...newRecipe, batch_size_unit: e.target.value })}
                sx={{ flex: 1 }}
              />
            </Box>
            <Alert severity="info" sx={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
              Creates a new draft MBR with no process steps. You can add steps after creation.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1F2937' }}>
          <Button onClick={() => setNewRecipeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newRecipe.mbr_number || !newRecipe.product_code || !newRecipe.batch_size_value}
            onClick={async () => {
              try {
                const prod = products.find(p => p.product_code === newRecipe.product_code);
                const res = await fetch('/api/master-batch-records/', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    mbr_number: newRecipe.mbr_number,
                    product: prod?.id,
                    version: 1,
                    batch_size_value: parseFloat(newRecipe.batch_size_value),
                    batch_size_unit: newRecipe.batch_size_unit,
                    status: 'draft',
                    created_by: 'admin',
                  }),
                });
                if (res.ok) {
                  setSnackbar({ open: true, message: `Created ${newRecipe.mbr_number}`, severity: 'success' });
                  setNewRecipeOpen(false);
                  setNewRecipe({ mbr_number: '', product_code: '', product_name: '', batch_size_value: '', batch_size_unit: 'tablets', version: '1' });
                  fetchMasterBatchRecords();
                } else {
                  const err = await res.text();
                  setSnackbar({ open: true, message: `Failed: ${err}`, severity: 'error' });
                }
              } catch (e) {
                setSnackbar({ open: true, message: 'Create failed', severity: 'error' });
              }
            }}
          >
            Create Recipe
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Preview Dialog */}
      <Dialog open={importOpen && !!importData} onClose={() => { setImportOpen(false); setImportData(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1F2937' }}>
          <UploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Import Recipe — {importFilename}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1F2937' }}>
          {importData && (
            <Box sx={{ mt: 2 }}>
              {importData._format === 'bha-ebr-mbr-v1' ? (
                <Alert severity="success" sx={{ mb: 2, backgroundColor: 'rgba(16,185,129,0.1)' }}>
                  Valid BHA EBR format detected
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'rgba(245,158,11,0.1)' }}>
                  Unknown format — will attempt import
                </Alert>
              )}
              <Box sx={{ p: 2, backgroundColor: '#111827', borderRadius: 1 }}>
                <Typography variant="body2"><strong>MBR Number:</strong> {importData.mbr?.mbr_number || '—'}</Typography>
                <Typography variant="body2"><strong>Product:</strong> {importData.mbr?.product_name || '—'} ({importData.mbr?.product_code || '—'})</Typography>
                <Typography variant="body2"><strong>Batch Size:</strong> {importData.mbr?.batch_size_value || '—'} {importData.mbr?.batch_size_unit || ''}</Typography>
                <Typography variant="body2"><strong>Process Steps:</strong> {importData.process_steps?.length || 0}</Typography>
                <Typography variant="body2"><strong>Total Parameters:</strong> {importData.process_steps?.reduce((sum: number, s: any) => sum + (s.parameters?.length || 0), 0) || 0}</Typography>
                {importData.workflow_definition && (
                  <Typography variant="body2"><strong>Workflow Definition:</strong> Included</Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1F2937' }}>
          <Button onClick={() => { setImportOpen(false); setImportData(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleImportConfirm}>
            Import Recipe
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MBREditor;